import React, { useEffect, useState } from "react"
import {
  Alert,
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import { useIcdSignNewAgreementMutation } from "store/api/investorapi/icds.api"
import Spinners from "components/Common/Spinner"
import {
  formatAmount,
  formatAmountInWords,
  sanitizeIntegerInput,
} from "helpers/common"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  useIcdSignNewAgreementWpMutation,
  useLazyGetAssignedInvestorsListQuery,
} from "store/api/wealthpartnerapi/referrals/referrals.api"

const defaultValues = {
  tenure: "36",
  amount: "",
}

function SignAgreementModal({
  open,
  setOpen,
  assignedInvestors = false,
  showAlert = false,
}) {
  const navigate = useNavigate()
  const [submit, { isLoading }] = useIcdSignNewAgreementMutation()
  const [submitWp, { isLoading: isLoadingWp }] =
    useIcdSignNewAgreementWpMutation()
  const [getAssignedInvestors, { isFetching }] =
    useLazyGetAssignedInvestorsListQuery()
  const [data, setData] = useState([])

  const validation = useFormik({
    initialValues: { ...defaultValues },
    validationSchema: Yup.object({}),
    validateOnMount: false,
    onSubmit: async (values) => {
      onFormUpdate(values)
    },
  })

  useEffect(() => {
    if (assignedInvestors) {
      getInvestors()
    }
  }, [assignedInvestors])

  const getInvestors = async () => {
    try {
      const { data } = await getAssignedInvestors().unwrap()
      setData(data)
      validation.setFieldValue("investor_id", "")
    } catch (error) {}
  }

  const onFormUpdate = async (values) => {
    try {
      const { message } = assignedInvestors
        ? await submitWp({ ...values }).unwrap()
        : await submit({ ...values }).unwrap()

      setOpen(false)
      navigate(
        assignedInvestors
          ? "/investor-management/agreements"
          : "/profile/documents/icd-agreements"
      )
      toast.success(message, {
        id: "toasterMessage",
      })
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  return (
    <Modal
      isOpen={open}
      toggle={() => {
        setOpen((prev) => !prev)
      }}
      size="md"
      centered={true}
    >
      <ModalHeader
        toggle={() => {
          setOpen((prev) => !prev)
        }}
      >
        Sign New Agreement
      </ModalHeader>
      <ModalBody>
        <div className="py-1">
          {showAlert && (
            <Alert color="warning">
              Your proposed ICD amount and expiry date do not match any of the
              past agreements. Please sign a new one to proceed with further
              investments.
            </Alert>
          )}
          <Form
            onSubmit={(e) => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            <Row className="mb-2">
              {assignedInvestors && (
                <Col xl={12}>
                  <Label className="col-form-label">
                    Select Name of Entity<span className="error">*</span>
                  </Label>
                  <select
                    id="investor_id"
                    name="investor_id"
                    className="form-select"
                    placeholder="Select Name of Entity"
                    value={validation.values.investor_id}
                    onChange={validation.handleChange}
                  >
                    <option value=""> Select Name of Entity</option>
                    {data?.map((item, index) => (
                      <option key={`investor_${index}`} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                  {validation.errors.investor_id ? (
                    <FormFeedback type="invalid">
                      {validation.errors.investor_id}
                    </FormFeedback>
                  ) : null}
                </Col>
              )}
              <Col xl={12}>
                <Label className="col-form-label">
                  ICD Tenure (in Months)<span className="error">*</span>
                </Label>
                <Input
                  disabled
                  name="tenure"
                  type="number"
                  className="form-control"
                  placeholder="Tenure"
                  min={1}
                  value={validation.values.tenure}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.tenure && validation.errors.tenure
                      ? true
                      : false
                  }
                />

                {validation.touched.tenure && validation.errors.tenure ? (
                  <FormFeedback type="invalid">
                    {validation.errors.tenure}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col xl={12}>
                <Label className="col-form-label">
                  Proposed ICD Amount <span className="error">*</span>
                </Label>
                <Input
                  name="amount"
                  type="text"
                  className="form-control"
                  placeholder="Proposed ICD Amount"
                  min={1}
                  value={`₹ ${formatAmount(validation.values.amount)}`}
                  onChange={(e) =>
                    sanitizeIntegerInput(e, validation.setFieldValue, "amount")
                  }
                  invalid={
                    validation.touched.amount && validation.errors.amount
                      ? true
                      : false
                  }
                />

                <p className="text-dark" style={{}}>
                  Indian Rupees{" "}
                  {formatAmountInWords(validation.values.amount || 0)} Only
                </p>
                <span
                  className="text-muted"
                  style={{
                    fontStyle: "italic",
                  }}
                >
                  The Proposed ICD Amount is a non-binding indicative limit and
                  does not create any obligation to invest in Prachay Capital
                  Limited.
                </span>
                {validation.touched.amount && validation.errors.amount ? (
                  <FormFeedback type="invalid">
                    {validation.errors.amount}
                  </FormFeedback>
                ) : null}
              </Col>
              <div className="mt-3 py-1 button-wrapper">
                <Button className="cancel" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" color="primary">
                  {isLoading || isLoadingWp ? <Spinners /> : "Proceed"}
                </Button>
              </div>
            </Row>
          </Form>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default SignAgreementModal
